/* The profile form's two halves. `useSettings` is re-exported from shared so
   the feature's own code has one import path for both; the query itself is
   shared because the whole app reads it. */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { patchSettings, settingsKeys } from "@/shared/api/settings";

import type { ProfilePatch, Settings, User } from "@/shared/types";

export { useSettings } from "@/shared/api/settings";

/** The three editable fields, as the form holds them: never null, because an
    empty input is `""`. `profilePatch` maps them back to the wire. */
export interface ProfileForm {
  first_name: string;
  last_name: string;
  timezone: string;
}

export const formOf = (user: User): ProfileForm => ({
  first_name: user.first_name ?? "",
  last_name: user.last_name ?? "",
  timezone: user.timezone ?? "",
});

/** Only what actually changed: PATCH reads a present key as an intent to write,
    so sending the whole form would rewrite fields the user never touched. An
    emptied input clears the field (`null`), it does not write `""`. */
export function profilePatch(user: User, form: ProfileForm): ProfilePatch {
  const patch: ProfilePatch = {};
  for (const key of ["first_name", "last_name", "timezone"] as const) {
    const next = form[key].trim() || null;
    if (next !== user[key]) patch[key] = next;
  }
  return patch;
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: patchSettings,
    // Optimistic on `user.*` only — plan, org and onboarding are not this
    // mutation's to guess at, and the invalidate below refreshes them anyway.
    onMutate: async (patch: ProfilePatch) => {
      await qc.cancelQueries({ queryKey: settingsKeys.all });
      const previous = qc.getQueryData<Settings>(settingsKeys.all);
      if (previous) {
        qc.setQueryData<Settings>(settingsKeys.all, { ...previous, user: { ...previous.user, ...patch } });
      }
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) qc.setQueryData(settingsKeys.all, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}
